import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./MyAttendancePreview.css";
import { selectStyles } from "../../../assets/Styles/selectStyle";
import { fetchMonths } from "../../../services/monthlyAttendanceService";

const MyAttendancePreview = () => {
    const currentDate = new Date();

    // ---- State ----
    const [attendanceData, setAttendanceData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [month, setMonth] = useState(currentDate.getMonth() + 1); // default current month
    const [year, setYear] = useState(currentDate.getFullYear()); // default current year
    const [monthOptions, setMonthOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const employeeId = localStorage.getItem("employeeId");
    const [isFocused, setIsFocused] = useState({ month: false, year: false });
    const yearInputRef = useRef(null);
    const [calendarDate, setCalendarDate] = useState(new Date(year, month - 1));


    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentDate.getFullYear(), 11, 31);

    // ---- Load months for selected year ----
    useEffect(() => {
        const loadMonths = async () => {
            try {
                let months = await fetchMonths(year);

                // Ensure current month is included if year === current year
                const currentYear = currentDate.getFullYear();
                const currentMonth = currentDate.getMonth() + 1;

                if (year === currentYear && !months.find(m => m.value === currentMonth)) {
                    months = [
                        ...months,
                        { label: currentDate.toLocaleString("default", { month: "long" }), value: currentMonth }
                    ];
                    months.sort((a, b) => a.value - b.value);
                }

                setMonthOptions(months);

                // Default to current month if not present
                if (!months.find(m => m.value === month)) {
                    setMonth(currentMonth);
                }
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors(prev => ({ ...prev, general: "Failed to load months" }));
            }
        };

        if (year) loadMonths();
    }, [year]);

    // ---- Fetch attendance data automatically ----
    useEffect(() => {
        const fetchAttendance = async () => {
            if (!employeeId || !month || !year) return;

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/attendance/${employeeId}/${year}/${month}`
                );
                const data = Array.isArray(response.data) ? response.data : [response.data];
                setAttendanceData(data);
            } catch (error) {
                console.error("Error fetching attendance data:", error);
                setErrors(prev => ({ ...prev, general: "Failed to fetch attendance data" }));
                alert("Failed to fetch attendance data. Please try again.");
            }
        };

        fetchAttendance();
    }, [year, month, employeeId]);

    // ---- Handlers ----
const handleYearChange = (date) => {
    const newYear = date ? date.getFullYear() : null;

    // Always set month to current month when year changes
    const currentMonth = currentDate.getMonth() + 1;

    setYear(newYear);
    setMonth(currentMonth);

    // Set calendar to current month of chosen year
    setCalendarDate(new Date(newYear, currentMonth - 1));

    setErrors(prev => ({ ...prev, year: null }));
};


    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;

        if (field === "month") {
            setMonth(value);
            setCalendarDate(new Date(year, value - 1));
        }

        setErrors(prev => ({ ...prev, [field]: null }));
    };


    const handleDateClick = (date) => {
        setSelectedDate(date.toISOString().split("T")[0]);
    };

    // ---- Calendar tile coloring ----
    const tileClassName = ({ date, view }) => {
        if (view !== "month") return "";

        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const record = attendanceData.find(a => a.attendanceDate === formattedDate);

        if (!record) return "calendar-tile-no-data";
        if (record.weekOff) return "calendar-tile-weekoff";
        if (record.holiday) return "calendar-tile-holiday";
        if (record.present) return "calendar-tile-present";
        if (record.absent) return "calendar-tile-absent";

        return "calendar-tile-no-data";
    };

    // ---- Calculate total present and absent days ----
    const totalPresentDays = attendanceData.filter(a => a.present).length;
    const totalAbsentDays = attendanceData.filter(a => a.absent).length;
    const totalHolidays = attendanceData.filter(a => a.holiday).length;
    const totalWeekOffs = attendanceData.filter(a => a.weekOff).length;

    return (
        <div>
            {/* ---- Filter Section ---- */}
            <div className="myAttendance-container">
                <div className="myAttendance-box">
                    <h2 className="myAttendance-heading">My Attendance</h2>

                    <form>
                        <div className="myAttendance-maincontainer">
                            {/* Year Picker */}
                            <div className="myAttendance-container-part">
                                <div className={`myAttendance-floating-label-container ${isFocused.year || year ? "float-label" : ""}`}>
                                    <DatePicker
                                        selected={year ? new Date(year, 0, 1) : null}
                                        onChange={handleYearChange}
                                        onFocus={() => setIsFocused(p => ({ ...p, year: true }))}
                                        onBlur={() => setIsFocused(p => ({ ...p, year: false }))}
                                        dateFormat="yyyy"
                                        showYearPicker
                                        placeholderText=""
                                        className="myAttendance-date-picker"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        ref={yearInputRef}
                                    />
                                    <label><span className="required">*</span>Year</label>
                                </div>
                            </div>

                            {/* Month Dropdown */}
                            <div className="myAttendance-container-part">
                                <div className={`myAttendance-floating-label-container ${isFocused.month || month ? "float-label" : ""}`}>
                                    <Select
                                        name="month"
                                        options={monthOptions}
                                        value={monthOptions.find(opt => opt.value === month) || null}
                                        onChange={option => handleInputChange(option, "month")}
                                        onFocus={() => setIsFocused(p => ({ ...p, month: true }))}
                                        onBlur={() => setIsFocused(p => ({ ...p, month: false }))}
                                        placeholder=" "
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                    <label><span className="required">*</span>Month</label>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* ---- Calendar Section ---- */}
            <div className="calendar-section">
                <div className="width-cal">
                    <Calendar
                        className="attendance-calendar"
                        tileClassName={tileClassName}
                        onClickDay={handleDateClick}
                        activeStartDate={calendarDate}   // 👈 Controlled month/year display
                        maxDate={new Date()}
                        minDate={new Date(2000, 0, 1)}
                    />

                    {/* <Calendar
                        className="attendance-calendar"
                        tileClassName={tileClassName}
                        onClickDay={handleDateClick}
                        maxDate={new Date()} // prevent future dates
                        minDate={new Date(2000, 0, 1)} // earliest selectable date
                        tileContent={({ date, view }) => {
                            if (view === "month") {
                                const formattedDate = `${date.getFullYear()}-${String(
                                    date.getMonth() + 1
                                ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

                                const record = attendanceData.find(
                                    (a) => a.attendanceDate === formattedDate
                                );

                                // ✅ STRICT check — only show tooltip if holiday === true
                                if (record && record.holiday === true && !!record.holidayName) {
                                    return (
                                        <span
                                            title={record.holidayName}
                                            style={{
                                                cursor: "pointer",
                                                display: "inline-block",
                                             
                                            }}
                                        />
                                    );
                                }
                            }
                            return null; // ❌ ensures other dates show nothing
                        }}
                    /> */}

                    {/* Legend */}
                    <div className="calendar-legend">
                        <div className="legend-item">
                            <div className="legend-color calendar-tile-weekoff"></div>
                            <span>Sunday</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color calendar-tile-holiday"></div>
                            <span>Holiday</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color calendar-tile-present"></div>
                            <span>Present</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color calendar-tile-absent"></div>
                            <span>Absent</span>
                        </div>

                    </div>

                    {/* ✅ Totals */}
                    <div style={{ marginTop: "10px" }}>
                        <span>Total Present Days :<strong> {totalPresentDays}</strong>

                        </span>
                        <span style={{ marginLeft: '20px' }}>
                            Total Absent Days : <strong> {totalAbsentDays} </strong>
                        </span>
                    </div>
                    <div style={{ marginTop: "10px" }}>
                        <span>Total Holidays :<strong> {totalHolidays}</strong>

                        </span>
                        <span style={{ marginLeft: '50px' }}>
                            Total weekOff  : <strong> {totalWeekOffs} </strong>
                        </span>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default MyAttendancePreview;
